import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Clock, MapPin, Phone, Mail, Heart, Sparkles, Menu, X, ChevronLeft, ChevronRight, ChevronDown, Check, CalendarDays, Lock, Leaf, Star, Instagram, Facebook, Youtube, Hand, Flower2, Eye, Feather, Paintbrush, Ticket } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { api, formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { ChatWidget } from "../components/ChatWidget";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const HERO_IMG = "/site-hero.jpg";
const ABOUT_IMG = "/site-about.jpg";
const LOCATION_IMG = "/nelleke-salon.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const eur = (n) => "€ " + n.toFixed(2).replace(".", ",").replace(",00", ",-");

const DAY_NAMES = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag", "Zondag"];

const NAV = [
  { id: "over", label: "Over Nelleke" },
  { id: "behandelingen", label: "Behandelingen" },
  { id: "reviews", label: "Reviews" },
  { id: "reserveren", label: "Reserveren" },
  { id: "contact", label: "Contact" },
];

const SALON_ADDRESS = ["Hondsdrafweg 24", "2381 JC Zoeterwoude"];
const SALON_PHONE = "0628856762";
const SOCIALS = [
  { icon: Instagram, href: "https://www.instagram.com/nellekebeautymassage/", label: "Instagram" },
  { icon: Facebook, href: "https://www.facebook.com/nellekeholtbeautymassage/", label: "Facebook" },
  { icon: Youtube, href: "https://www.youtube.com/@nellekeholt7481", label: "YouTube" },
];

function GoogleG({ className }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function PublicSite() {
  const [data, setData] = useState({ categories: [], treatments: [] });
  const [hours, setHours] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], average: 0, count: 0 });
  const [gallery, setGallery] = useState([]);
  const [booking, setBooking] = useState({ enabled: true, message: "" });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/public/treatments").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/opening-hours").then((r) => {
      setHours([...r.data.opening_hours].sort((a, b) => a.day - b.day));
      setOverrides(r.data.overrides || []);
      setBooking({ enabled: r.data.booking_enabled !== false, message: r.data.booking_disabled_message || "" });
    }).catch(() => {});
    api.get("/public/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/public/gallery").then((r) => setGallery(r.data)).catch(() => {});
    const hash = window.location.hash.replace("#", "");
    if (hash) setTimeout(() => scrollTo(hash), 400);
  }, []);

  return (
    <div className="min-h-screen bg-cream text-ink font-sans overflow-x-hidden">
      {/* GOOGLE REVIEW RIBBON */}
      {reviews.count > 0 && (
        <a href="/reviews" data-testid="google-review-ribbon"
          className="fixed top-0 inset-x-0 z-[60] h-9 bg-brand text-white flex items-center justify-center gap-2 text-xs sm:text-sm hover:bg-brand-hover transition-colors">
          <span className="w-4 h-4 rounded-full bg-white flex items-center justify-center shrink-0"><GoogleG className="w-3 h-3" /></span>
          <span className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(reviews.average) ? "fill-white text-white" : "text-white/40"}`} strokeWidth={1.5} />
            ))}
          </span>
          <span className="font-semibold">{reviews.average.toFixed(1).replace(".", ",")}</span>
          <span className="text-white/90">op Google</span>
          <span className="hidden sm:inline text-white/70">· Beoordeeld door tevreden klanten</span>
        </a>
      )}
      {/* NAV */}
      <header className={`fixed inset-x-0 z-50 backdrop-blur-xl bg-cream/80 border-b border-[#E8E2D9] ${reviews.count > 0 ? "top-9" : "top-0"}`}>
        <div className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
          <button onClick={() => scrollTo("top")} data-testid="nav-logo"
            className="flex items-center gap-2.5 font-display text-lg text-ink">
            <img src="/logo-round.png" alt="Nelleke Holt" className="w-9 h-9" />
            <span>Nelleke Holt</span>
          </button>
          <nav className="hidden md:flex items-center gap-9">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`nav-${n.id}`}
                className="text-sm text-ink-muted hover:text-brand transition-colors duration-300">
                {n.label}
              </button>
            ))}
            {booking.enabled && (
              <Button onClick={() => scrollTo("reserveren")} data-testid="nav-reserve-btn"
                className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
                Afspraak
              </Button>
            )}
            <a href="/admin/login" data-testid="nav-admin-btn" title="Beheer" aria-label="Beheer"
              className="w-10 h-10 rounded-full border border-[#E8E2D9] flex items-center justify-center text-ink-muted hover:text-brand hover:border-brand hover:-translate-y-0.5 transition-all duration-300">
              <Lock className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </nav>
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-cream border-t border-[#E8E2D9] px-5 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => { scrollTo(n.id); setMenuOpen(false); }}
                className="text-left text-ink-muted">{n.label}</button>
            ))}
            <a href="/admin/login" data-testid="nav-admin-btn-mobile"
              className="flex items-center gap-2 text-ink-muted pt-2 border-t border-[#E8E2D9]">
              <Lock className="w-4 h-4" strokeWidth={1.5} /> Beheer
            </a>
          </div>
        )}
      </header>

      <span id="top" />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="animate-blob absolute -top-24 -left-16 w-72 h-72 bg-brand-light/70 blur-3xl opacity-70" />
          <div className="animate-blob absolute top-16 right-0 w-80 h-80 bg-[#F7E3D8]/70 blur-3xl opacity-60" style={{ animationDelay: "-7s" }} />
          <div className="animate-floaty-slow absolute top-28 left-1/2 text-brand/30"><Sparkles className="w-8 h-8" strokeWidth={1.5} /></div>
          <div className="animate-floaty absolute bottom-24 left-8 text-brand/20"><Sparkles className="w-6 h-6" strokeWidth={1.5} /></div>
          <div className="animate-floaty-slow absolute top-40 right-16 text-brand/20" style={{ animationDelay: "-3s" }}><Sparkles className="w-5 h-5" strokeWidth={1.5} /></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 text-brand text-sm mb-6 bg-brand-light px-4 py-1.5 rounded-full">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Salon aan huis · Zoeterwoude-Dorp
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
              Even helemaal
              <span className="block gradient-text italic">tot rust komen</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink-muted max-w-md">
              Persoonlijke aandacht, zorgvuldige behandelingen en een warme, rustige sfeer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => scrollTo("reserveren")} data-testid="hero-reserve-btn"
                className="rounded-full bg-brand hover:bg-brand-hover text-white px-8 h-12 hover:-translate-y-0.5 transition-transform duration-300">
                Reserveer een afspraak
              </Button>
              <Button onClick={() => scrollTo("behandelingen")} variant="outline" data-testid="hero-treatments-btn"
                className="rounded-full border-[#E8E2D9] text-ink px-8 h-12 bg-white/60 hover:bg-white">
                Bekijk behandelingen
              </Button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }} className="relative">
            <div className="animate-spin-slow absolute -z-10 -inset-6 rounded-full border border-dashed border-brand/20" />
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgb(74,63,63,0.12)]">
              <img src={HERO_IMG} alt="Ontspannen behandeling" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="animate-floaty absolute -bottom-5 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3 border border-[#E8E2D9]">
              <img src="/logo-round.png" alt="" className="w-9 h-9" />
              <div className="text-xs leading-tight">
                <div className="font-medium text-ink">Nelleke Holt</div>
                <div className="text-ink-muted">Beauty &amp; Massage</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#E8E2D9] bg-white/50 py-4 overflow-hidden marquee-mask">
        <div className="marquee-track gap-10 font-display text-lg sm:text-xl text-brand/80">
          {Array.from({ length: 2 }).map((_, r) => (
            <div key={r} className="flex items-center gap-10 pr-10">
              {["Ontspanning", "Persoonlijke aandacht", "Rust", "Zorg & precisie", "Massages", "Schoonheid", "Even tijd voor jou"].map((w, i) => (
                <span key={i} className="flex items-center gap-10 whitespace-nowrap">
                  {w}<span className="text-brand/40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section id="over" className="py-20 md:py-28 bg-cream-subtle/50">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-14 items-center">
          <div className="rounded-[2rem] overflow-hidden order-2 md:order-1 shadow-[0_12px_40px_rgb(74,63,63,0.08)]">
            <img src={ABOUT_IMG} alt="Nelleke" className="w-full h-80 md:h-[440px] object-cover" />
          </div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="order-1 md:order-2">
            <span className="text-sm uppercase tracking-widest text-brand">Even voorstellen</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">
              Behandelingen met aandacht en zorg
            </h2>
            <div className="mt-6 space-y-4 text-ink-muted leading-relaxed">
              <p>
                Hoi, ik ben Nelleke en ik heb een salon aan huis in Zoeterwoude-Dorp. Ik vind persoonlijk contact met mijn klanten belangrijk. Ook ben ik een zorgzaam persoon dat vertrouwen belangrijk vind en ik werk op mijn eigen manier, waarbij ik voor elke klant en behandeling rustig de tijd neem en met aandacht en precisie werk. Voor mij is het belangrijk dat elke behandeling niet alleen een mooi en gewenst resultaat geeft, maar ook een moment van ontspanning.
              </p>
              <p>
                Daarnaast is er tijd voor de behandeling om eventuele vragen te stellen, en daarna om even bij te komen.
              </p>
              <p>
                De ruimte is ingericht met warme en zachte tinten, rustige verlichting en een kalme sfeer, zodat het een ontspannen beleving kan zijn.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-6">
              {[
                { icon: Heart, t: "Persoonlijk" },
                { icon: Sparkles, t: "Zorgvuldig" },
                { icon: Leaf, t: "Rustgevend" },
              ].map((f) => (
                <div key={f.t} className="flex items-center gap-2 text-ink">
                  <f.icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
                  <span className="text-sm">{f.t}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TREATMENTS */}
      <section id="behandelingen" className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-5">
          <div className="text-center mb-14">
            <span className="text-sm uppercase tracking-widest text-brand">Prijslijst</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Behandelingen</h2>
            <p className="mt-3 text-sm text-ink-muted max-w-md mx-auto">De aangegeven tijd (± min) is indicatief. Ik werk rustig en naar inzicht, zodat elke behandeling alle aandacht krijgt.</p>
          </div>
          <div className="space-y-4">
            {data.categories.map((cat, idx) => {
              const items = data.treatments.filter((t) => t.category === cat);
              if (!items.length) return null;
              return <PriceCategory key={cat} cat={cat} items={items} defaultOpen={idx === 0} />;
            })}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.count > 0 && (
        <section id="reviews" className="py-20 md:py-28 bg-cream-subtle/50">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center mb-12">
              <span className="text-sm uppercase tracking-widest text-brand">Ervaringen</span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Wat klanten zeggen</h2>
              <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full border border-[#E8E2D9] px-6 py-3 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
                <GoogleG className="w-6 h-6" />
                <span className="text-2xl font-medium">{reviews.average.toFixed(1).replace(".", ",")}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= Math.round(reviews.average) ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
                  ))}
                </span>
                <span className="text-sm text-ink-muted">· Beoordeeld op Google</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.reviews.slice(0, 6).map((r, i) => (
                <motion.div key={r.id} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                  data-testid={`home-review-${r.id}`}
                  className="bg-white rounded-2xl border border-[#E8E2D9] p-6 shadow-[0_8px_30px_rgb(74,63,63,0.04)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgb(74,63,63,0.12)]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-brand-light text-brand flex items-center justify-center font-display text-lg shrink-0">
                      {r.author?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.author}</div>
                      <div className="text-xs text-ink-muted flex items-center gap-1">
                        <GoogleG className="w-3.5 h-3.5" /> {r.source || "Google"}{r.date_label ? ` · ${r.date_label}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
                    ))}
                  </div>
                  {r.text && <p className="text-sm text-ink-muted leading-relaxed line-clamp-5">{r.text}</p>}
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-10">
              <a href="/reviews" data-testid="all-reviews-link"
                className="inline-flex items-center gap-1 rounded-full border border-[#E8E2D9] bg-white px-6 py-3 text-sm text-ink hover:border-brand hover:text-brand transition-colors">
                Bekijk meer reviews <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* INSTAGRAM / SFEERBLOK */}
      {gallery.length > 0 && (
        <section id="sfeer" className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-5">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
              <div>
                <span className="text-sm uppercase tracking-widest text-brand">Behandelingen</span>
                <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Indruk van de behandelingen</h2>
              </div>
              <a href="https://www.instagram.com/nellekebeautymassage/" target="_blank" rel="noopener noreferrer"
                data-testid="instagram-follow-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm hover:bg-brand-hover hover:-translate-y-0.5 transition-all duration-300 w-fit">
                <Instagram className="w-4 h-4" strokeWidth={1.5} /> Volg op Instagram
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {gallery.slice(0, 12).map((g, i) => (
                <motion.a key={g.id} href="https://www.instagram.com/nellekebeautymassage/" target="_blank" rel="noopener noreferrer"
                  initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                  data-testid={`gallery-item-${g.id}`}
                  className={`group relative overflow-hidden rounded-2xl aspect-square ${i % 5 === 0 ? "sm:col-span-2 sm:row-span-2 sm:aspect-auto" : ""}`}>
                  <img src={`${API}/public/gallery/${g.id}/image`} alt={g.caption || "Sfeerfoto salon"}
                    loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors duration-500 flex items-center justify-center">
                    <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" strokeWidth={1.5} />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RESERVE + LOCATION */}
      <section id="reserveren" className="py-20 md:py-28 bg-cream-subtle/50">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-14">
          <ReservationForm treatments={data.treatments.filter((t) => t.schedulable)} categories={data.categories} hours={hours} overrides={overrides} reviews={reviews} booking={booking} />
          <div>
            <span className="text-sm uppercase tracking-widest text-brand">Hoe het werkt</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Reserveren</h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              Kies een behandeling en geef je voorkeurstijd door. Ik plan de
              afspraak handmatig in en bevestig deze per e-mail met een
              agenda-uitnodiging. Zo weet je zeker dat er alle tijd voor je is.
            </p>
            <div className="mt-8 rounded-[2rem] overflow-hidden shadow-[0_12px_40px_rgb(74,63,63,0.08)] relative">
              <img src={LOCATION_IMG} alt="Nelleke Holt in haar salon" className="w-full h-96 object-cover object-[50%_30%]" />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/60 to-transparent p-4">
                <span className="text-white font-display text-lg">Nelleke</span>
                <span className="text-white/80 text-sm block">Jouw specialist bij Beauty &amp; Massage</span>
              </div>
            </div>
            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3 text-brand">
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm uppercase tracking-widest">Openingstijden</span>
              </div>
              <ul className="space-y-1.5 text-sm max-w-xs">
                {hours.map((h) => (
                  <li key={h.day} className="flex justify-between gap-4">
                    <span className="text-ink">{DAY_NAMES[h.day]}</span>
                    <span className={h.enabled ? "text-ink" : "text-ink-muted"}>
                      {h.enabled ? `${h.start} – ${h.end}` : "Gesloten"}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 mt-5 text-sm">
                <MapPin className="w-4 h-4 text-brand" strokeWidth={1.5} />
                <span>Zoeterwoude-Dorp</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-14">
          <div>
            <span className="text-sm uppercase tracking-widest text-brand">Vragen?</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight">Neem contact op</h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              Heb je een vraag over een behandeling of wil je meer informatie?
              Laat gerust een bericht achter, ik neem zo snel mogelijk contact
              met je op.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-brand mt-0.5" strokeWidth={1.5} />
                <span className="text-ink">{SALON_ADDRESS[0]}<br />{SALON_ADDRESS[1]}</span></div>
              <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-brand" strokeWidth={1.5} />
                <a href={`tel:+31${SALON_PHONE.replace(/^0/, "")}`} className="text-ink hover:text-brand transition-colors" data-testid="contact-phone-link">{SALON_PHONE}</a></div>
              <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-brand" strokeWidth={1.5} />
                <a href="mailto:info@nellekeholt.nl" className="text-ink hover:text-brand transition-colors" data-testid="contact-email-link">info@nellekeholt.nl</a></div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  data-testid={`social-${s.label.toLowerCase()}`} aria-label={s.label} title={s.label}
                  className="w-11 h-11 rounded-full border border-[#E8E2D9] bg-white flex items-center justify-center text-ink-muted hover:text-brand hover:border-brand hover:-translate-y-0.5 transition-all duration-300">
                  <s.icon className="w-5 h-5" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-ink text-white/80 py-12">
        <div className="max-w-6xl mx-auto px-5 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <div className="flex items-center gap-2.5 font-display text-lg text-white">
              <img src="/logo-round.png" alt="" className="w-8 h-8" /> Nelleke Holt Beauty &amp; Massage
            </div>
            <p className="mt-2 text-sm text-white/60">Salon aan huis · {SALON_ADDRESS[0]}, {SALON_ADDRESS[1]}</p>
            <div className="mt-4 flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
                  data-testid={`footer-social-${s.label.toLowerCase()}`} aria-label={s.label} title={s.label}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-brand hover:bg-brand/20 transition-all duration-300">
                  <s.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
          <div className="text-sm text-white/60 flex items-end gap-5">
            <a href="/reviews" className="hover:text-brand transition-colors">Reviews</a>
            <a href="/planner" data-testid="planner-link" className="hover:text-brand transition-colors">Planner</a>
            <a href="/admin/login" data-testid="admin-link" className="hover:text-brand transition-colors">Beheer</a>
          </div>
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}

const STEPS = ["Behandeling", "Datum & tijd", "Je gegevens"];
const eurFmt = (n) => "€ " + n.toFixed(2).replace(".", ",").replace(",00", ",-");

function catIcon(cat) {
  const c = (cat || "").toLowerCase();
  if (c.includes("massage")) return Hand;
  if (c.includes("schoonheid")) return Flower2;
  if (c.includes("wenkbrauw") || c.includes("wimper")) return Eye;
  if (c.includes("hars")) return Feather;
  if (c.includes("gellak") || c.includes("nagel")) return Paintbrush;
  if (c.includes("ritten") || c.includes("kaart")) return Ticket;
  return Sparkles;
}

function PriceCategory({ cat, items, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const prices = items.map((t) => t.price || 0).filter((p) => p > 0);
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const Icon = catIcon(cat);
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
      className="bg-white rounded-2xl border border-[#E8E2D9] shadow-[0_8px_30px_rgb(74,63,63,0.04)] overflow-hidden transition-colors duration-300 hover:border-brand/40">
      <button onClick={() => setOpen(!open)} data-testid={`price-cat-${cat}`} aria-expanded={open}
        className="w-full flex items-center gap-4 p-5 md:p-6 text-left">
        <span className="w-11 h-11 rounded-full bg-brand-light flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-brand" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg sm:text-xl font-medium text-brand leading-tight break-words">{cat}</h3>
          <p className="text-sm text-ink-muted mt-0.5">
            {items.length} behandeling{items.length > 1 ? "en" : ""}{minPrice ? ` · vanaf ${eur(minPrice)}` : ""}
          </p>
        </div>
        <span className={`w-9 h-9 rounded-full bg-brand-light/60 flex items-center justify-center shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}>
          <ChevronDown className="w-5 h-5 text-brand" strokeWidth={2} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="overflow-hidden">
            <ul className="divide-y divide-[#F2EBE1] px-5 md:px-6 pb-5 md:pb-6 border-t border-[#F2EBE1]">
              {items.map((t) => (
                <li key={t.id} data-testid={`treatment-row-${t.id}`}
                  className="flex items-start justify-between gap-4 py-4">
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-ink">{t.name}</span>
                      {cat !== "Rittenkaarten" && (
                        <span className="text-xs text-ink-muted whitespace-nowrap flex items-center gap-1" title="Tijd is indicatief; ik werk rustig en naar inzicht.">
                          <Clock className="w-3 h-3" /> ± {t.duration_min} min
                        </span>
                      )}
                    </div>
                    {t.description && <p className="text-sm text-ink-muted mt-1 leading-snug">{t.description}</p>}
                  </div>
                  <span className="font-medium text-ink whitespace-nowrap">{eur(t.price)}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ReservationForm({ treatments, categories, hours, overrides, reviews, booking }) {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("");
  const [treatmentId, setTreatmentId] = useState("");
  const [date, setDate] = useState(null);
  const [timeMode, setTimeMode] = useState("specific");
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [fullDays, setFullDays] = useState(new Set());
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const cats = useMemo(
    () => (categories || []).filter((c) => treatments.some((t) => t.category === c)),
    [categories, treatments]
  );
  const catTreatments = useMemo(
    () => treatments.filter((t) => t.category === category),
    [treatments, category]
  );
  const selectedTreatment = treatments.find((t) => t.id === treatmentId);

  useEffect(() => {
    const from = format(new Date(), "yyyy-MM-dd");
    const to = format(new Date(Date.now() + 120 * 86400000), "yyyy-MM-dd");
    api.get(`/public/full-days?start=${from}&end=${to}`)
      .then((r) => setFullDays(new Set(r.data.dates || [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!date || !treatmentId) { setSlots(null); return; }
    let cancel = false;
    setSlotsLoading(true);
    const ds = format(date, "yyyy-MM-dd");
    api.get(`/public/availability?date=${ds}&treatment_id=${treatmentId}`)
      .then((r) => { if (!cancel) { const sl = r.data.slots || []; setSlots(sl); setT1((prev) => sl.includes(prev) ? prev : ""); setT2((prev) => sl.includes(prev) ? prev : ""); } })
      .catch(() => { if (!cancel) setSlots([]); })
      .finally(() => { if (!cancel) setSlotsLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, treatmentId]);

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isDayOpen = (day) => {
    const ds = format(day, "yyyy-MM-dd");
    const ov = (overrides || []).find((o) => o.date === ds);
    if (ov) return ov.mode === "open";
    const md = (day.getDay() + 6) % 7;
    const h = (hours || []).find((x) => x.day === md);
    return h ? h.enabled : true;
  };

  const timeLabel = () => {
    if (!date) return "";
    const d = format(date, "EEEE d MMMM yyyy", { locale: nl });
    let tp = "";
    if (timeMode === "specific") tp = t1 ? `om ${t1}` : "";
    if (timeMode === "around") tp = t1 ? `rond ${t1}` : "";
    if (timeMode === "between") tp = t1 && t2 ? `tussen ${t1} en ${t2}` : "";
    const s = `${d} ${tp}`.trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const canNext = () => {
    if (step === 1) return category && treatmentId;
    if (step === 2) {
      if (!date || !slots || slots.length === 0) return false;
      if (timeMode === "between") return !!t1 && !!t2 && slots.includes(t1) && slots.includes(t2) && t2 > t1;
      return !!t1 && slots.includes(t1);
    }
    return true;
  };

  const next = () => { if (!canNext()) { toast.error("Vul dit stapje eerst in."); return; } setStep(Math.min(step + 1, 3)); };
  const back = () => setStep(Math.max(step - 1, 1));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone) { toast.error("Naam, e-mail en telefoon zijn verplicht."); return; }
    setLoading(true);
    try {
      await api.post("/public/reservation", {
        ...form,
        treatment_id: treatmentId,
        treatment_name: selectedTreatment?.name,
        preferred_time: timeLabel(),
        requested_start: (timeMode === "specific" && date && t1) ? `${format(date, "yyyy-MM-dd")}T${t1}:00` : undefined,
        requested_date: date ? format(date, "yyyy-MM-dd") : undefined,
        requested_duration: selectedTreatment?.duration_min,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const reset = () => {
    setDone(false); setStep(1); setCategory(""); setTreatmentId(""); setDate(null);
    setTimeMode("specific"); setT1(""); setT2(""); setSlots(null); setForm({ name: "", email: "", phone: "", message: "" });
  };

  if (booking && booking.enabled === false) {
    return (
      <div data-testid="reservation-closed" className="bg-white rounded-2xl border border-[#E8E2D9] p-8 md:p-10 shadow-[0_8px_30px_rgb(74,63,63,0.04)] text-center">
        <div className="flex justify-center mb-4"><div className="w-14 h-14 rounded-full bg-brand-light flex items-center justify-center"><Clock className="w-7 h-7 text-brand" strokeWidth={1.5} /></div></div>
        <h3 className="text-2xl font-medium mb-3">Reserveren tijdelijk gesloten</h3>
        <p className="text-ink-muted leading-relaxed mb-6 max-w-md mx-auto whitespace-pre-line">{booking.message || "Online reserveren is momenteel even niet mogelijk. Binnenkort kun je hier weer een afspraak inplannen."}</p>
        <Button onClick={() => scrollTo("contact")} data-testid="res-closed-contact" className="rounded-full bg-brand hover:bg-brand-hover text-white px-8">
          Neem contact op
        </Button>
      </div>
    );
  }

  if (done) {
    return (
      <div data-testid="reservation-form" className="bg-white rounded-2xl border border-[#E8E2D9] p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)] text-center">
        <div className="flex justify-center mb-4"><div className="w-14 h-14 rounded-full bg-brand-light flex items-center justify-center"><Check className="w-7 h-7 text-brand" /></div></div>
        <h3 className="text-2xl font-medium mb-2">Aanvraag verstuurd</h3>
        <p className="text-ink-muted leading-relaxed mb-6">Bedankt voor je aanvraag! Dit is nog geen definitieve afspraak. Ik doe mijn best om aan je verzoek tegemoet te komen en je krijgt van mij bericht zodra ik de afspraak heb ingepland en bevestigd.</p>
        <Button onClick={reset} data-testid="res-new" className="rounded-full bg-brand hover:bg-brand-hover text-white px-8">Nieuwe aanvraag</Button>
      </div>
    );
  }

  return (
    <div data-testid="reservation-form" className="bg-white rounded-2xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
      {reviews?.count > 0 && (
        <a href="/reviews" data-testid="reservation-trust-bar"
          className="flex items-center gap-2.5 mb-5 -mt-1 rounded-xl bg-cream-subtle/70 border border-[#E8E2D9] px-3.5 py-2.5 hover:border-brand/40 transition-colors">
          <GoogleG className="w-5 h-5 shrink-0" />
          <span className="text-sm font-medium text-ink">Beoordeeld met {reviews.average.toFixed(1).replace(".", ",")}</span>
          <span className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-3.5 h-3.5 ${n <= Math.round(reviews.average) ? "fill-amber-400 text-amber-400" : "text-[#E8E2D9]"}`} strokeWidth={1.5} />
            ))}
          </span>
          <span className="text-xs text-ink-muted ml-auto whitespace-nowrap">Reviews op Google</span>
        </a>
      )}
      <h3 className="text-2xl font-medium mb-1">Reserveringsaanvraag</h3>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6 mt-4">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const active = n === step, past = n < step;
          return (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs shrink-0 transition-colors duration-300 ${
                active ? "bg-brand text-white" : past ? "bg-brand-light text-brand" : "bg-cream-subtle text-ink-muted"}`}>
                {past ? <Check className="w-3.5 h-3.5" /> : n}
              </div>
              {i < STEPS.length - 1 && <div className={`h-px flex-1 ${past ? "bg-brand" : "bg-[#E8E2D9]"}`} />}
            </div>
          );
        })}
      </div>
      <p className="text-sm text-ink-muted mb-5">Stap {step} van 3 · {STEPS[step - 1]}</p>

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
          transition={{ duration: 0.25 }} className="space-y-4 min-h-[220px]">

          {step === 1 && (
            <>
              <div>
                <Label className="text-sm text-ink-muted mb-2 block">Type behandeling</Label>
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => (
                    <button key={c} type="button" data-testid={`res-category-${c}`}
                      onClick={() => { setCategory(c); setTreatmentId(""); }}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 max-w-full break-words text-left ${
                        category === c ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              {category && (
                <div>
                  <Label className="text-sm text-ink-muted mb-1.5 block">Behandeling</Label>
                  <Select value={treatmentId} onValueChange={setTreatmentId}>
                    <SelectTrigger data-testid="res-treatment" className="rounded-xl border-[#E8E2D9]"><SelectValue placeholder="Kies een behandeling" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {catTreatments.map((t) => (
                        <SelectItem key={t.id} value={t.id} disabled={!!t.paused} data-testid={`res-treatment-option-${t.id}`}>
                          {t.name} · {t.duration_min} min · {eurFmt(t.price)}{t.paused ? " — (Tijdelijk niet mogelijk)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div>
                <Label className="text-sm text-ink-muted mb-1.5 block">Voorkeursdatum</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="res-date-trigger"
                      className="w-full justify-start rounded-xl border-[#E8E2D9] font-normal">
                      <CalendarDays className="w-4 h-4 mr-2 text-brand" />
                      {date ? format(date, "EEEE d MMMM yyyy", { locale: nl }) : "Kies een datum"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} locale={nl}
                      onSelect={(day) => { if (day && fullDays.has(format(day, "yyyy-MM-dd"))) { toast.error("Deze dag is al vol — kies een andere datum."); return; } setDate(day); }}
                      disabled={(day) => day < today || !isDayOpen(day)}
                      modifiers={{ full: (day) => fullDays.has(format(day, "yyyy-MM-dd")) }}
                      modifiersClassNames={{ full: "line-through text-ink-muted opacity-50" }} />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-ink-muted mt-1.5">Gesloten en volgeboekte dagen zijn niet selecteerbaar.</p>
              </div>
              <div>
                <Label className="text-sm text-ink-muted mb-2 block">Voorkeur tijd</Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { id: "specific", label: "Specifieke tijd" },
                    { id: "around", label: "Rond een uur" },
                    { id: "between", label: "Tussen ... en ..." },
                  ].map((m) => (
                    <button key={m.id} type="button" data-testid={`res-timemode-${m.id}`}
                      onClick={() => setTimeMode(m.id)}
                      className={`px-4 py-2 rounded-full text-sm border transition-colors duration-200 ${
                        timeMode === m.id ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
                <div data-testid="res-slots">
                  {!date || !treatmentId ? (
                    <p className="text-sm text-ink-muted">Kies eerst een datum hierboven.</p>
                  ) : slotsLoading ? (
                    <p className="text-sm text-ink-muted">Beschikbare tijden laden…</p>
                  ) : !slots || slots.length === 0 ? (
                    <p data-testid="res-no-slots" className="text-sm text-ink-muted rounded-xl bg-cream-subtle/60 border border-[#E8E2D9] px-3.5 py-3">
                      Geen vrije tijden op deze dag. Kies een andere datum of een andere behandeling.
                    </p>
                  ) : timeMode === "specific" ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button key={s} type="button" data-testid={`res-slot-${s}`} onClick={() => setT1(s)}
                          className={`px-2 py-2 rounded-xl text-sm border transition-colors duration-200 ${
                            t1 === s ? "bg-brand text-white border-brand" : "bg-white text-ink border-[#E8E2D9] hover:border-brand"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  ) : timeMode === "around" ? (
                    <Select value={t1} onValueChange={setT1}>
                      <SelectTrigger data-testid="res-around-select" className="rounded-xl border-[#E8E2D9] max-w-[220px]"><SelectValue placeholder="Kies een vrij tijdstip" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {slots.map((s) => <SelectItem key={s} value={s} data-testid={`res-around-${s}`}>rond {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select value={t1} onValueChange={(v) => { setT1(v); if (t2 && v >= t2) setT2(""); }}>
                        <SelectTrigger data-testid="res-between-from" className="rounded-xl border-[#E8E2D9] w-[120px]"><SelectValue placeholder="Van" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {slots.map((s) => <SelectItem key={s} value={s} data-testid={`res-between-from-${s}`}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <span className="text-ink-muted">en</span>
                      <Select value={t2} onValueChange={setT2}>
                        <SelectTrigger data-testid="res-between-to" className="rounded-xl border-[#E8E2D9] w-[120px]"><SelectValue placeholder="Tot" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {slots.filter((s) => !t1 || s > t1).map((s) => <SelectItem key={s} value={s} data-testid={`res-between-to-${s}`}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {slots && slots.length > 0 && (
                    <p className="text-xs text-ink-muted mt-2">Alleen vrije tijden zijn te kiezen — bezette en geblokkeerde tijden zie je hier niet.</p>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="rounded-xl bg-cream-subtle/60 p-4 text-sm">
                <div className="font-medium">{selectedTreatment?.name}</div>
                <div className="text-ink-muted mt-0.5">{timeLabel()}</div>
              </div>
              <Field label="Naam *"><Input data-testid="res-name" value={form.name} onChange={set("name")} placeholder="Je naam" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="E-mail *"><Input data-testid="res-email" type="email" value={form.email} onChange={set("email")} placeholder="je@email.nl" /></Field>
                <Field label="Telefoon *"><Input data-testid="res-phone" value={form.phone} onChange={set("phone")} placeholder="+31 6 ..." /></Field>
              </div>
              <Field label="Bericht (optioneel)"><Textarea data-testid="res-message" value={form.message} onChange={set("message")} placeholder="Aanvullende wensen..." rows={2} /></Field>
              <div data-testid="res-request-note" className="rounded-xl bg-brand-light/60 border border-brand/20 p-4 text-sm text-ink leading-relaxed">
                <strong>Let op:</strong> dit is een <strong>aanvraag</strong>, nog geen definitieve afspraak. Ik doe mijn best om aan je verzoek tegemoet te komen en je krijgt van mij bericht zodra ik de afspraak heb ingepland en bevestigd.
              </div>
            </>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer buttons */}
      <div className="flex items-center justify-between gap-3 mt-6 pt-5 border-t border-[#F2EBE1]">
        {step > 1 ? (
          <Button type="button" variant="outline" onClick={back} data-testid="res-back"
            className="rounded-full border-[#E8E2D9]"><ChevronLeft className="w-4 h-4 mr-1" /> Terug</Button>
        ) : <span />}
        {step < 3 ? (
          <Button type="button" onClick={next} data-testid="res-next"
            className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">Volgende <ChevronRight className="w-4 h-4 ml-1" /></Button>
        ) : (
          <Button type="button" onClick={submit} disabled={loading} data-testid="res-submit"
            className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            {loading ? "Versturen..." : "Aanvraag versturen"}</Button>
        )}
      </div>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Vul alle velden in (naam, e-mail, telefoon en bericht).");
      return;
    }
    setLoading(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Bedankt voor je bericht! Ik neem snel contact op.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form"
      className="bg-white rounded-2xl border border-[#E8E2D9] p-8 shadow-[0_8px_30px_rgb(74,63,63,0.04)]">
      <div className="space-y-4">
        <Field label="Naam *"><Input data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Je naam" /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="E-mail *"><Input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="je@email.nl" /></Field>
          <Field label="Telefoon *"><Input data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+31 6 ..." /></Field>
        </div>
        <Field label="Bericht *"><Textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Je vraag..." rows={4} /></Field>
        <Button type="submit" disabled={loading} data-testid="contact-submit"
          className="w-full rounded-full bg-brand hover:bg-brand-hover text-white h-12">
          {loading ? "Versturen..." : "Verstuur bericht"}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-ink-muted mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
